import { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Button } from "reactstrap"
import { compress } from "@quicktoolsone/pdf-compress"
import { toast } from "react-toastify"
import { pdfjs } from "utils/pdfWorker"
import { uploadImages } from "api/imageApi"
import FileViewerModal from "components/Common/FileViewerModal"

export const MAX_PDF_SIZE_BYTES = 5 * 1024 * 1024

export const formatFileSize = size => {
  const bytes = Number(size || 0)

  if (!bytes) return "-"

  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const attachmentOriginalSize = attachment =>
  attachment.original_size || attachment.real_size || attachment.size

const attachmentCompressedSize = attachment =>
  attachment.compressed_size || (!isPendingAttachment(attachment) ? attachment.size : null)

const isPendingAttachment = attachment => !!attachment.file

const isPdfAttachment = attachment => {
  const mime = String(attachment?.mime_type || "").toLowerCase()
  if (mime === "application/pdf") return true

  const name = String(attachment?.original_name || attachment?.filename || "").toLowerCase()
  return name.endsWith(".pdf")
}

const formatPageCount = pageCount => {
  const pages = Number(pageCount || 0)

  if (!pages) return "Pages: -"

  return `${pages} ${pages === 1 ? "page" : "pages"}`
}

const getPdfPageCount = async file => {
  const buffer = await file.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data: buffer }).promise

  return pdf.numPages
}

const compressedPdfFileName = fileName => {
  const normalizedName = fileName || "document.pdf"

  return normalizedName.toLowerCase().endsWith(".pdf")
    ? normalizedName.replace(/\.pdf$/i, "-compressed.pdf")
    : `${normalizedName}-compressed.pdf`
}

export const compressPdfFile = async attachment => {
  const originalFile = attachment.file
  const originalBuffer = await originalFile.arrayBuffer()
  let compressedBlob = null

  try {
    const balancedResult = await compress(originalBuffer, {
      preset: "balanced",
      timeout: 300000,
    })
    compressedBlob = new Blob([balancedResult.pdf], { type: "application/pdf" })

    if (compressedBlob.size > MAX_PDF_SIZE_BYTES) {
      const maxResult = await compress(originalBuffer, {
        preset: "max",
        timeout: 300000,
      })
      const maxBlob = new Blob([maxResult.pdf], { type: "application/pdf" })

      if (maxBlob.size < compressedBlob.size) {
        compressedBlob = maxBlob
      }
    }
  } catch (error) {
    if (originalFile.size > MAX_PDF_SIZE_BYTES) {
      throw new Error(
        `${originalFile.name} could not be compressed. Please upload a smaller PDF.`
      )
    }
  }

  const shouldUseCompressed =
    compressedBlob &&
    (compressedBlob.size < originalFile.size ||
      originalFile.size > MAX_PDF_SIZE_BYTES)
  const uploadFile = shouldUseCompressed
    ? new File([compressedBlob], compressedPdfFileName(originalFile.name), {
        type: "application/pdf",
        lastModified: Date.now(),
      })
    : originalFile

  if (uploadFile.size > MAX_PDF_SIZE_BYTES) {
    throw new Error(
      `${originalFile.name} is still ${formatFileSize(
        uploadFile.size
      )} after compression. Please upload a smaller PDF.`
    )
  }

  return {
    uploadFile,
    original_size: originalFile.size,
    compressed_size: uploadFile.size,
  }
}

export const uploadPendingPdfAttachments = async attachments => {
  const pendingAttachments = attachments.filter(isPendingAttachment)
  const savedAttachments = attachments.filter(
    attachment => !isPendingAttachment(attachment)
  )

  if (!pendingAttachments.length) {
    return savedAttachments
  }

  const prepared = []
  for (const attachment of pendingAttachments) {
    const isPdf = String(
      attachment.mime_type ||
        attachment.file.type ||
        attachment.file.name ||
        ""
    )
      .toLowerCase()
      .includes("pdf")

    if (isPdf) {
      const compressed = await compressPdfFile(attachment)
      prepared.push({
        attachment,
        uploadFile: compressed.uploadFile,
        original_size: compressed.original_size,
        compressed_size: compressed.compressed_size,
      })
    } else {
      prepared.push({
        attachment,
        uploadFile: attachment.file,
        original_size: attachment.size,
        compressed_size: attachment.size,
      })
    }
  }

  const formData = new FormData()
  prepared.forEach(attachment => {
    formData.append("images[]", attachment.uploadFile)
  })

  const res = await uploadImages(formData)
  const uploaded = (res?.data || [])
    .filter(
      file =>
        file.mime_type === "application/pdf" ||
        String(file.mime_type || "").startsWith("image/")
    )
    .map((file, index) => {
      const pending = pendingAttachments[index] || {}
      const item = prepared[index] || {}

      return {
        ...file,
        original_size:
          item.original_size || pending.original_size || file.size,
        compressed_size: item.compressed_size || file.size,
        page_count: pending.page_count || null,
      }
    })

  return [...savedAttachments, ...uploaded]
}

const PdfAttachmentUpload = ({
  attachments = [],
  disabled = false,
  maxFiles = 3,
  onChange,
}) => {
  const [processing, setProcessing] = useState(false)
  const [viewerFile, setViewerFile] = useState(null)

  const handleFileChange = useCallback(
    async e => {
      const files = Array.from(e.target.files || [])
      e.target.value = ""
      if (!files.length) return

      const invalidFile = files.find(file => {
        const type = String(file.type || "").toLowerCase()
        return !(
          type === "application/pdf" ||
          type.startsWith("image/") ||
          String(file.name || "").toLowerCase().endsWith(".pdf")
        )
      })
      if (invalidFile) {
        toast.error("Only image or PDF attachments are allowed.")
        return
      }

      if (attachments.length + files.length > maxFiles) {
        toast.error(`You can attach up to ${maxFiles} file(s).`)
        return
      }

      setProcessing(true)
      try {
        const pendingFiles = await Promise.all(
          files.map(async (file, index) => {
            const isPdf =
              String(file.type || "").toLowerCase() === "application/pdf" ||
              String(file.name || "").toLowerCase().endsWith(".pdf")
            let pageCount = null

            if (isPdf) {
              pageCount = await getPdfPageCount(file).catch(() => {
                toast.warn(`Could not read page count for ${file.name}.`)
                return null
              })
            }

            return {
              temp_id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
              file,
              filename: file.name,
              original_name: file.name,
              mime_type: file.type,
              original_size: file.size,
              compressed_size: file.size,
              size: file.size,
              page_count: pageCount,
              is_pending: true,
            }
          })
        )
        onChange?.([...attachments, ...pendingFiles])
      } catch (error) {
        toast.error("Failed to prepare attachment.")
      } finally {
        setProcessing(false)
      }
    },
    [attachments, maxFiles, onChange]
  )

  const handleRemove = index => {
    onChange?.(attachments.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleView = attachment => {
    if (attachment.url) {
      setViewerFile({
        url: attachment.url,
        name: attachment.original_name || attachment.filename || "PDF",
        revokeOnClose: false,
      })
      return
    }

    if (attachment.file) {
      setViewerFile({
        url: URL.createObjectURL(attachment.file),
        name: attachment.original_name || attachment.filename || "PDF",
        revokeOnClose: true,
      })
    }
  }

  const handleCloseViewer = () => {
    if (viewerFile?.revokeOnClose) {
      URL.revokeObjectURL(viewerFile.url)
    }
    setViewerFile(null)
  }

  return (
    <div>
      {!disabled && (
        <div className="alert alert-info py-2 px-3 mb-2 small">
          Select the scanned original LC document files (PDF or image) for this
          vehicle acknowledgement. Files are uploaded only after you click save;
          review size here before submitting. PDFs are compressed on save and
          the final uploaded PDF must be
          {` ${formatFileSize(MAX_PDF_SIZE_BYTES)} or smaller.`}
        </div>
      )}
      {attachments.length > 0 && (
        <div className="d-flex flex-column gap-1 mb-2">
          {attachments.map((attachment, index) => (
            <div
              key={attachment.id || attachment.url || attachment.temp_id || index}
              className="d-flex align-items-center justify-content-between gap-2 border rounded px-2 py-1 bg-light"
            >
              <button
                type="button"
                className="btn btn-link text-start text-truncate p-0"
                title={attachment.original_name || attachment.filename || "Attachment"}
                onClick={() => handleView(attachment)}
              >
                {isPdfAttachment(attachment) ? (
                  <i className="mdi mdi-file-pdf-box text-danger me-1" />
                ) : (
                  <i className="mdi mdi-file-image-outline text-success me-1" />
                )}
                {attachment.original_name || attachment.filename || "Attachment"}
              </button>
              {isPendingAttachment(attachment) && (
                <span className="badge badge-soft-warning flex-shrink-0">
                  Upload on save
                </span>
              )}
              <div className="text-muted small flex-shrink-0">
                {isPdfAttachment(attachment) && (
                  <>
                    {formatPageCount(attachment.page_count)}
                    <span className="mx-1">|</span>
                  </>
                )}
                Real: {formatFileSize(attachmentOriginalSize(attachment))}
                <span className="mx-1">|</span>
                Compressed:{" "}
                {isPendingAttachment(attachment)
                  ? "On save"
                  : formatFileSize(attachmentCompressedSize(attachment))}
              </div>
              {!disabled && (
                <Button
                  type="button"
                  color="link"
                  className="text-danger p-0"
                  onClick={() => handleRemove(index)}
                >
                  <i className="mdi mdi-close" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {!disabled && attachments.length < maxFiles && (
        <input
          type="file"
          accept="image/*,.pdf"
          multiple
          className="form-control form-control-sm"
          onChange={handleFileChange}
          disabled={processing}
        />
      )}
      {processing && (
        <div className="text-muted small mt-1">
          Reading file details...
        </div>
      )}
      <FileViewerModal
        isOpen={!!viewerFile}
        onClose={handleCloseViewer}
        fileUrl={viewerFile?.url || ""}
        fileName={viewerFile?.name || "Attachment"}
      />
    </div>
  )
}

PdfAttachmentUpload.propTypes = {
  attachments: PropTypes.array,
  disabled: PropTypes.bool,
  maxFiles: PropTypes.number,
  onChange: PropTypes.func,
}

export default PdfAttachmentUpload
